/**
 * Total snapshots — rule 5 of `docs/determinism.md`.
 *
 * > A snapshot captures everything, including timers, the clock, pending
 * > commands and RNG state. Restoring and continuing must be indistinguishable
 * > from never having stopped.
 *
 * This is the rule the other five are for. Determinism means a season can be
 * *reproduced* from its seed; a snapshot means it can be *resumed* from its
 * middle — which is what a settlement worker does when it picks up a district it
 * has never seen, and what a client does when a mispredicted command has to be
 * rolled back (ADR-002 D7).
 *
 * ### What a snapshot is not
 *
 * It is not a save file. The client's save (`SaveLoadSystem`) is allowed to be
 * lossy, because a player loading one is starting a session rather than
 * continuing a season, and it carries things a district does not have — a camera,
 * a toolbar, a slot name. A `DistrictSnapshot` is the season and nothing else.
 *
 * It is not configuration either. Nothing in here says how fast concrete cures,
 * how hard a slab hits a chamber, or what a fruit is worth. Those are world
 * parameters that a season is settled *under*, and the Charter (design §17) is
 * where they will eventually live. A snapshot restored under different parameters
 * is a different world — honestly so, and detectably, because the hash will say
 * as much.
 *
 * ### A snapshot preserves order; it does not canonicalise
 *
 * The rule that is easiest to get wrong, and it is the exact opposite of the rule
 * for hashing. `canonicalState()` sorts every collection by id, because two runs
 * that decided the same things must hash the same however their `Map`s were
 * filled. A snapshot must do the reverse: `Map` iteration order is *state*.
 *
 * The surface ants are the proof. Their ids are colony ant ids, so ants from three
 * colonies interleave as they surface, and the controller draws one value per ant
 * per tick from a shared stream. Writing them out sorted by id restores a world
 * that agrees on every ant and then hands them each other's dice. It hashes
 * equal, it snapshots equal, and it diverges on the first tick — which is why
 * `tests/snapshot.test.ts` runs restored worlds on for thousands of ticks rather
 * than trusting a comparison taken at the moment of restore.
 */

import { Canonical, hashState } from './hashState';
import type { ColonySnapshot } from './colony/ColonySim';
import type { SurfaceSnapshot } from './surface/SurfaceSim';

/**
 * The snapshot format's version.
 *
 * One number for every snapshot in a district, because they are written and read
 * as one document. Bump it when a field's *meaning* changes; adding a field that
 * an older reader can default is not a bump, and neither is adding one a newer
 * reader requires — that is a bump, since an old snapshot can no longer be read.
 *
 * 2 adds the district's Chronicle (ADR-012). A version 1 snapshot would carry no
 * `surface.season`, and defaulting it to an empty Chronicle would restore a
 * district whose tick says four seasons have passed and whose public record says
 * none did — exactly the silent, subtly-wrong load this module refuses. No
 * migration is owed (ADR-005 D22).
 *
 * 3 adds the grid connection (ADR-013): the standing allocation, the shed set,
 * and the brownout ledger. Defaulting it would restore a district with every
 * stalled site building again and with no record of the cycles its families
 * spent dark — and the allocation in particular is a *national input*, which
 * §4 says a district's behaviour is a pure function of. A snapshot that has lost
 * one is a snapshot of a different world.
 *
 * 4 adds the mains (ADR-014): the allocation, **the rainfall**, the restriction
 * sets and the ledger of cycles spent dry. The rainfall is the sharpest case
 * this rule has produced — a district restored at rainfall 1 is a district whose
 * drought ended because somebody reloaded, and it would go on settling seasons
 * without complaint.
 *
 * 5 adds the federal estate (ADR-015): the facilities on the map, their
 * footprints and their uptime ledgers. A version 4 snapshot's terrain can carry
 * `SEALED` cells — the grid stores whatever state it was in — so defaulting the
 * estate to empty would restore a district with a slab on it, no facility, no
 * load, and twenty-four cells the mayor cannot build on and nobody can explain.
 *
 * 6 adds the labour market (ADR-016): the roster, **the strike**, and the wage
 * ledger, plus a facility's staffing fraction and the two ends of a commuting
 * car's trip. The strike is this version's rainfall — a district restored with
 * everybody back at their desks is a district whose labour dispute ended because
 * somebody reloaded, and the uptime it went on recording would be a measurement
 * of something that did not happen. The roster matters for a duller reason and an
 * equally fatal one: hire order depends on who already has a job, so a district
 * that forgot who works where would re-hire a different eight families and pay
 * different people from the next cycle on.
 *
 * 7 adds the rent and the shift (ADR-017): the treasury's lease receipts, the
 * estate's payments, and the set of families who were standing in a workplace
 * when the last cycle settled. The last of those is the one that would be silent
 * — wages are paid against presence now, so a district restored with an empty
 * shift set skips a cycle's payroll and nothing could ever say why.
 *
 * 8 adds the households (ADR-018): each family's accumulated exposure to the
 * dark and to dry taps, their strain streak, and the district's migration ledger
 * by cause. This one is the brownout's rainfall. A district restored with the
 * exposure forgotten is a district whose blackout un-happened: the families the
 * measure was three cycles from losing stay, the season record goes on reporting
 * departures that will now never come, and the only trace of the difference is
 * that the population is wrong.
 *
 * 9 changes the shape of that same block (ADR-019). A household's distance from
 * giving up is a reservoir rather than a streak, and the arrival gate has a
 * ledger of its own. A version 8 snapshot's exposure tuples are one field wider
 * than this build reads, and the streak it carries is not the quantity the
 * reservoir holds — restoring one would put every strained household back at a
 * patience the measure never gave them.
 *
 * 10 adds the district's own works (ADR-020): the covenants, the saplings and the
 * cycle each was planted on, the undercrossings, and the ledger of what all of it
 * cost — plus, in the terrain, the cells whose bush somebody planted. Two of those
 * three are invisible without it, and invisibly so. A covenanted cell is `NATURAL`,
 * so a version 9 snapshot of a district with a deeded meadow restores as a district
 * with an ordinary meadow: the next brush stroke paves ground the mayor had promised
 * to leave alone, and nothing anywhere would report a difference. A planted bush is
 * `NATURAL` too, and would come back as grass — with its food source deleted on the
 * first tick, which is a colony losing a feeding station because somebody reloaded.
 *
 * 11 adds the district's account with the polity (ADR-021): the tithe rate, what it
 * still owes outside itself, the ledger of bills and levies — and, load-bearing above
 * all of it, the three *marks* the bills and the tithe are subtractions against. A
 * version 10 snapshot has no marks, so a district restored from one bills itself for
 * every kilowatt it has ever drawn and remits a tithe on the whole history of its own
 * economy, in a single cycle. Silently, because the government pool is one number and
 * nothing announces where it went.
 *
 * 12 adds the corridor (ADR-022): the interchanges the mayor built, the freight the
 * polity routes over them, the cursor that decides which pair the next leg runs
 * between — and the mark the tolls and the road wear are subtractions against, which
 * fails exactly the way version 11's three do and on a quantity that is not money. A
 * version 11 snapshot has no interchanges, so a district restored from one has its
 * corridor demolished and its trucks left running legs to structures that are no
 * longer there. It also widens every vehicle by one field: a truck restored without
 * its cargo is a truck that forgets where it was going and joins the local traffic
 * forever.
 *
 * 13 adds the world outside (ADR-025): the two prices this district has been
 * quoted, the sales ledger, and — inside the estate — what each facility has
 * produced and what producing it cost. The two prices are why this is a version
 * bump rather than a widening. A version 12 snapshot has no posted prices, so a
 * district restored from one trades at the price of last resort: the same compute
 * fetches whatever this build ships instead of whatever the world had quoted, and
 * the treasury borrows at the config's rate rather than at the world's. Both are
 * silent, both are wrong by however much the world had moved, and the only trace
 * would be a Chronicle whose seasons stop adding up.
 *
 * 14 adds construction (ADR-029): a facility now has an opening date as well as a
 * siting date, a building record beside its uptime ledger, and a mark for the
 * lorries whose wear has already been charged to the roads. The market gains a
 * goods price and what has been spent at it, and a truck gains the address it is
 * delivering to. Every one of those is the same failure if it is dropped, and it
 * is the loudest one this format has: a version 13 snapshot has no `openedTick`,
 * so a district restored from one has a data centre that is neither open nor
 * building — and the two that would show it are a mark at zero, which wears the
 * roads by every lorry that ever delivered, and a hoard at zero, which starts the
 * build again.
 */
export const SNAPSHOT_VERSION = 14;

/**
 * A district, entire: the surface and the colonies beneath it.
 *
 * The unit ADR-003 D11 named as the unit of testing and ADR-001 costed as a
 * district-season. A settlement worker is handed one of these plus a command log
 * and produces the next one.
 */
export interface DistrictSnapshot {
  version: number;

  /**
   * The seed the district was built from.
   *
   * Carried so that a snapshot can be checked against the world it claims to
   * come from. Restoring does not *use* it — every generator's position is
   * captured directly, so a restored district does not need to know how it was
   * originally seeded — but a snapshot that cannot say which world it belongs to
   * is a snapshot nobody can file.
   */
  seed: string;

  /**
   * The surface's tick and sim-time, restated at the top level.
   *
   * Redundant with `surface`, deliberately: an operator listing snapshots, or a
   * scheduler deciding which one is newest, should not have to parse a district
   * to find out when it is.
   */
  tick: number;
  simTimeMs: number;

  surface: SurfaceSnapshot;
  colonies: ColonySnapshot[];
}

/**
 * Refuse a snapshot this build cannot read.
 *
 * Deliberately a throw rather than a best-effort load. A partially-understood
 * snapshot produces a district that is subtly not the one that was saved, and it
 * produces it silently — which is the failure mode every other rule in
 * `docs/determinism.md` exists to make impossible.
 */
export function requireSnapshotVersion(version: number, what: string): void {
  if (version !== SNAPSHOT_VERSION) {
    throw new RangeError(
      `${what} snapshot is version ${version}; this build reads version ` +
      `${SNAPSHOT_VERSION}`
    );
  }
}

/**
 * The two halves of a district, named by what a snapshot needs of them.
 *
 * Structural rather than the concrete `SurfaceSim` and `ColonySim`, so
 * that assembling a district snapshot does not require this module to import
 * either layer at runtime. There is no `District` class yet — the client wires
 * the halves together in `Game`, and the tests do it in `DistrictWorld` — so
 * these two functions are where the wiring's *snapshot* lives until there is an
 * object to hang it on.
 */
export interface SnapshotableSurface {
  snapshot(): SurfaceSnapshot;
  restore(state: SurfaceSnapshot): void;
}

export interface SnapshotableColony {
  readonly colonyIndex: number;
  snapshot(): ColonySnapshot;
  restore(state: ColonySnapshot): void;
}

/** Take a district's snapshot. Colonies in index order, which is how they update. */
export function snapshotDistrict(
  seed: string,
  surface: SnapshotableSurface,
  colonies: readonly SnapshotableColony[]
): DistrictSnapshot {
  const surfaceState = surface.snapshot();
  return {
    version: SNAPSHOT_VERSION,
    seed,
    tick: surfaceState.tick,
    simTimeMs: surfaceState.simTimeMs,
    surface: surfaceState,
    colonies: colonies.map((colony) => colony.snapshot()),
  };
}

/**
 * Restore a district from a snapshot.
 *
 * The colony count has to match, and mismatching it is an error rather than a
 * best effort: a district with two of its three colonies restored is a district
 * whose third colony is a fresh 847-ant settlement that has never heard of the
 * road above it, and it would settle a season without complaint.
 */
export function restoreDistrict(
  snapshot: DistrictSnapshot,
  surface: SnapshotableSurface,
  colonies: readonly SnapshotableColony[]
): void {
  requireSnapshotVersion(snapshot.version, 'district');
  if (snapshot.colonies.length !== colonies.length) {
    throw new RangeError(
      `district snapshot has ${snapshot.colonies.length} colonies; this district ` +
      `has ${colonies.length}`
    );
  }

  surface.restore(snapshot.surface);
  for (let i = 0; i < colonies.length; i++) {
    colonies[i].restore(snapshot.colonies[i]);
  }
}

/**
 * A snapshot's digest.
 *
 * Not the same thing as a state hash. `hashState(sim.canonicalState())` asks
 * *did two runs decide the same things*, and ignores what a car looks like. This
 * asks *are these the same document*, and does not ignore anything — which makes
 * it the check for whether a snapshot is total: snapshot, restore, snapshot
 * again, and the two digests must agree.
 *
 * Floats are rounded exactly as they are for a state hash, so the two checks
 * have the same tolerance and neither can pass on a rounding difference the
 * other would catch.
 */
export function hashSnapshot(snapshot: DistrictSnapshot): string {
  return hashState(snapshot as unknown as Canonical);
}

/**
 * A number on its way into a snapshot.
 *
 * JSON has no infinities and no `NaN`: `JSON.stringify(Infinity)` is `null`, and
 * a loader that assigned that `null` straight back would hand the simulation a
 * value that compares as zero and never complains. Since a snapshot has to
 * survive leaving the process, a non-finite quantity is stored as `null`
 * deliberately, and the load site says what `null` meant.
 *
 * There are two of these in a district today — a notification throttle that
 * starts at `-Infinity` and an ant's "no distance measured yet" — and both are
 * sentinels rather than arithmetic, which is why the round trip can be lossless.
 */
export function jsonNumber(value: number): number | null {
  return Number.isFinite(value) ? value : null;
}

/** The inverse of `jsonNumber`, with the sentinel spelled out by the caller. */
export function fromJsonNumber(value: number | null, whenNull: number): number {
  return value ?? whenNull;
}

/**
 * Round-trip a snapshot through JSON.
 *
 * The one thing a snapshot must survive that nothing else in the simulation has
 * to: leaving the process. A `Map`, a `Set`, an `undefined` or an `Infinity`
 * that reaches this function comes back changed or not at all, so this is the
 * test that a snapshot is a *document* rather than a heap of live objects that
 * happen to be reachable.
 */
export function throughJson(snapshot: DistrictSnapshot): DistrictSnapshot {
  return JSON.parse(JSON.stringify(snapshot)) as DistrictSnapshot;
}
